import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// SEO metadata for each route
const SEO_CONFIG = {
  '/': {
    title: 'Zero Deficit Sync | Smart Tax & Expense Tracker for Freelancers',
    description: 'Free tax and expense tracking for freelancers and small businesses. Track mileage, scan receipts with AI, generate Schedule C reports, and maximize your tax deductions.',
    keywords: 'tax tracker, expense tracker, freelancer taxes, Schedule C, mileage tracker, receipt scanner, small business accounting'
  },
  '/login': {
    title: 'Sign In | Zero Deficit Sync',
    description: 'Sign in to Zero Deficit Sync to access your tax and expense tracking dashboard. Free for all users.',
    keywords: 'login, sign in, zero deficit sync, tax tracker login'
  },
  '/pricing': {
    title: 'Pricing | Zero Deficit Sync - Free Tax Tracking',
    description: 'Zero Deficit Sync is completely free for all users. Track expenses, scan receipts, log mileage, and generate tax reports at no cost.',
    keywords: 'free tax tracker, free expense tracker, pricing, zero deficit sync'
  },
  '/business/dashboard': {
    title: 'Business Dashboard | Zero Deficit Sync',
    description: 'Your business financial dashboard. View income, expenses, profit metrics, and tax insights all in one place.',
    keywords: 'business dashboard, financial overview, profit tracking, business finances'
  },
  '/business/profit-loss': {
    title: 'Profit & Loss Statement | Zero Deficit Sync',
    description: 'Real-time P&L statement for your business. Track income vs expenses, view monthly trends, and analyze your profit margins.',
    keywords: 'profit and loss, P&L statement, income statement, business profitability'
  },
  '/business/expenses/manage': {
    title: 'Expense Management | Zero Deficit Sync',
    description: 'Manage your business expenses. Scan receipts with AI, import bank statements, and categorize expenses for tax deductions.',
    keywords: 'expense management, business expenses, receipt scanning, expense tracking'
  },
  '/business/income': {
    title: 'Income Tracking | Zero Deficit Sync',
    description: 'Track all your business income sources. Log payments, scan invoices, and monitor your revenue streams.',
    keywords: 'income tracking, revenue tracking, business income, payment tracking'
  },
  '/business/recurring-income': {
    title: 'Recurring Income Tracker | Zero Deficit Sync',
    description: 'Track retainers, subscriptions, and recurring revenue. Forecast your income and never miss a payment.',
    keywords: 'recurring income, retainer tracking, subscription revenue, revenue forecast'
  },
  '/business/invoices': {
    title: 'Invoice Generator | Zero Deficit Sync',
    description: 'Create professional invoices, track payments, and export to PDF. Manage your billing workflow efficiently.',
    keywords: 'invoice generator, create invoices, billing software, invoice tracking'
  },
  '/business/clients': {
    title: 'Client Management | Zero Deficit Sync',
    description: 'Manage your client database. Track client details, payment history, and outstanding balances.',
    keywords: 'client management, CRM, client database, customer tracking'
  },
  '/business/projects': {
    title: 'Project Costing | Zero Deficit Sync',
    description: 'Track project profitability. Assign expenses, log time, and analyze costs per project.',
    keywords: 'project costing, job costing, project profitability, project tracking'
  },
  '/business/contractors': {
    title: '1099 Contractor Tracking | Zero Deficit Sync',
    description: 'Track payments to contractors for 1099 reporting. Monitor YTD payments and generate tax summaries.',
    keywords: '1099 contractors, contractor payments, 1099 tracking, contractor management'
  },
  '/business/mileage': {
    title: 'Mileage Tracker | Zero Deficit Sync',
    description: 'Log business mileage for tax deductions. GPS tracking, frequent trips, and automatic IRS rate calculations.',
    keywords: 'mileage tracker, business mileage, IRS mileage rate, mileage deduction'
  },
  '/business/quarterly-taxes': {
    title: 'Quarterly Tax Estimator | Zero Deficit Sync',
    description: 'Calculate and track quarterly estimated tax payments. Self-employment tax, federal, and state tax estimates.',
    keywords: 'quarterly taxes, estimated taxes, self-employment tax, tax payments'
  },
  '/business/reports': {
    title: 'Financial Reports | Zero Deficit Sync',
    description: 'Generate tax reports, expense summaries, and Schedule C previews. Export to PDF, Excel, or CSV.',
    keywords: 'financial reports, tax reports, Schedule C, expense reports'
  },
  '/business/schedule-c': {
    title: 'Schedule C Generator | Zero Deficit Sync',
    description: 'Generate IRS Schedule C preview with all your business income and expenses automatically categorized.',
    keywords: 'Schedule C, IRS form, self-employment taxes, business tax form'
  },
  '/personal/dashboard': {
    title: 'Personal Finance Dashboard | Zero Deficit Sync',
    description: 'Your personal finance dashboard. Track income, expenses, budgets, and savings goals.',
    keywords: 'personal finance, budget tracker, savings goals, expense tracking'
  },
  '/personal/budgets': {
    title: 'Budget Tracker | Zero Deficit Sync',
    description: 'Set and track monthly budgets by category. Monitor spending and stay within your financial goals.',
    keywords: 'budget tracker, monthly budget, spending tracker, budget management'
  },
  '/personal/goals': {
    title: 'Savings Goals | Zero Deficit Sync',
    description: 'Set savings goals and track progress. Save for vacation, emergency fund, or any financial goal.',
    keywords: 'savings goals, financial goals, goal tracker, savings tracker'
  },
  '/personal/subscriptions': {
    title: 'Subscription Manager | Zero Deficit Sync',
    description: 'Track all your subscriptions and recurring bills. Get reminders before due dates.',
    keywords: 'subscription tracker, recurring bills, subscription management, bill reminders'
  },
  '/combined': {
    title: 'Combined Financial View | Zero Deficit Sync',
    description: 'View your complete financial picture. Merge business and personal finances for a holistic overview.',
    keywords: 'combined finances, financial overview, business and personal, net worth'
  },
  '/privacy': {
    title: 'Privacy Policy | Zero Deficit Sync',
    description: 'Zero Deficit Sync privacy policy. Learn how we protect your financial data and personal information.',
    keywords: 'privacy policy, data protection, security'
  },
  '/terms': {
    title: 'Terms of Service | Zero Deficit Sync',
    description: 'Zero Deficit Sync terms of service and user agreement.',
    keywords: 'terms of service, user agreement, terms and conditions'
  }
};

// Structured data for the application
const getStructuredData = (pathname) => {
  const baseData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Zero Deficit Sync",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Free tax and expense tracking for freelancers and small businesses",
    "url": "https://zerodeficitsync.com",
    "author": {
      "@type": "Organization",
      "name": "Zero Deficit Sync"
    },
    "featureList": [
      "Expense Tracking",
      "Mileage Logging",
      "Receipt Scanning with AI",
      "Invoice Generation",
      "Schedule C Reports",
      "Quarterly Tax Estimates",
      "Budget Tracking",
      "Savings Goals"
    ]
  };

  // Add breadcrumb for specific pages
  if (pathname.startsWith('/business/') || pathname.startsWith('/personal/')) {
    const parts = pathname.split('/').filter(Boolean);
    const breadcrumbItems = parts.map((part, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' '),
      "item": `https://zerodeficitsync.com/${parts.slice(0, index + 1).join('/')}`
    }));

    return [
      baseData,
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbItems
      }
    ];
  }

  return baseData;
};

function SEOHead() {
  const location = useLocation();
  
  useEffect(() => {
    const pathname = location.pathname;
    
    // Find matching SEO config (exact match or parent path)
    let seoData = SEO_CONFIG[pathname];
    
    if (!seoData) {
      // Try to find a parent path match
      const pathParts = pathname.split('/').filter(Boolean);
      while (pathParts.length > 0 && !seoData) {
        const parentPath = '/' + pathParts.join('/');
        seoData = SEO_CONFIG[parentPath];
        pathParts.pop();
      }
    }
    
    // Default SEO data
    if (!seoData) {
      seoData = SEO_CONFIG['/'];
    }
    
    // Update document title
    document.title = seoData.title;
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', seoData.description);
    }
    
    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', seoData.keywords);
    }
    
    // Update Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', seoData.title);
    }
    
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', seoData.description);
    }
    
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', `https://zerodeficitsync.com${pathname}`);
    }
    
    // Update Twitter tags
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', seoData.title);
    }
    
    let twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', seoData.description);
    }
    
    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `https://zerodeficitsync.com${pathname}`);
    }
    
    // Add/Update structured data
    let structuredDataScript = document.querySelector('script[type="application/ld+json"]');
    if (!structuredDataScript) {
      structuredDataScript = document.createElement('script');
      structuredDataScript.type = 'application/ld+json';
      document.head.appendChild(structuredDataScript);
    }
    structuredDataScript.textContent = JSON.stringify(getStructuredData(pathname));
    
  }, [location.pathname]);
  
  return null; // This component doesn't render anything
}

export default SEOHead;
